import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import {
  EMPTY_PAYMENT_SETTING_FORM,
  WITHDRAW_WEEKDAY_OPTIONS,
  paymentSettingContentToForm,
  paymentSettingFormToContent,
  toggleWithdrawDay,
} from './paymentSettingConstants';

export default function DepositWithdrawalParamSettingPage() {
  const [form, setForm] = useState(EMPTY_PAYMENT_SETTING_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  async function loadSetting() {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const data = await adminApi.getPaymentSettingConfig();
      setForm(paymentSettingContentToForm(data?.content));
    } catch (err) {
      setError(err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSetting();
  }, []);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const saved = await adminApi.savePaymentSettingConfig(paymentSettingFormToContent(form));
      setForm(paymentSettingContentToForm(saved?.content));
      setMessage('已保存');
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-card">
      <h2>充提參數設置</h2>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!loading && (
        <form className="settings-form" onSubmit={handleSubmit}>
          <section className="settings-section">
            <h3>充值限額</h3>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="depositMinPrice">充值最小金額</label>
                <input
                  id="depositMinPrice"
                  type="number"
                  min="0"
                  step="any"
                  value={form.depositMinPrice}
                  onChange={(e) => updateField('depositMinPrice', e.target.value)}
                  placeholder="留空表示不限制"
                />
              </div>
              <div className="form-field">
                <label htmlFor="depositMaxPrice">充值最大金額</label>
                <input
                  id="depositMaxPrice"
                  type="number"
                  min="0"
                  step="any"
                  value={form.depositMaxPrice}
                  onChange={(e) => updateField('depositMaxPrice', e.target.value)}
                  placeholder="留空表示不限制"
                />
              </div>
            </div>
          </section>

          <section className="settings-section">
            <h3>提現限額與手續費</h3>
            <p className="page-desc">金額 0 表示不限制；手續費為比例（如 0.01 = 1%）</p>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="withdrawMinPrice">提現最小金額</label>
                <input
                  id="withdrawMinPrice"
                  type="number"
                  min="0"
                  step="any"
                  value={form.withdrawMinPrice}
                  onChange={(e) => updateField('withdrawMinPrice', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="withdrawMaxPrice">提現最大金額</label>
                <input
                  id="withdrawMaxPrice"
                  type="number"
                  min="0"
                  step="any"
                  value={form.withdrawMaxPrice}
                  onChange={(e) => updateField('withdrawMaxPrice', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="withdrawFee">提現手續費比例</label>
                <input
                  id="withdrawFee"
                  type="number"
                  min="0"
                  step="any"
                  value={form.withdrawFee}
                  onChange={(e) => updateField('withdrawFee', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="withdrawSuccessLimitTimes">每日提現成功次數上限</label>
                <input
                  id="withdrawSuccessLimitTimes"
                  type="number"
                  min="0"
                  step="1"
                  value={form.withdrawSuccessLimitTimes}
                  onChange={(e) => updateField('withdrawSuccessLimitTimes', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="withdrawRejectLimitTimes">每日提現拒絕次數上限</label>
                <input
                  id="withdrawRejectLimitTimes"
                  type="number"
                  min="0"
                  step="1"
                  value={form.withdrawRejectLimitTimes}
                  onChange={(e) => updateField('withdrawRejectLimitTimes', e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="settings-section">
            <h3>開放提現時段</h3>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="startTime">開始時間</label>
                <input
                  id="startTime"
                  type="time"
                  value={form.startTime}
                  onChange={(e) => updateField('startTime', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="endTime">結束時間</label>
                <input
                  id="endTime"
                  type="time"
                  value={form.endTime}
                  onChange={(e) => updateField('endTime', e.target.value)}
                />
              </div>
            </div>

            <div className="form-field">
              <span className="form-label">允許提現星期</span>
              <div className="checkbox-row">
                {WITHDRAW_WEEKDAY_OPTIONS.map((item) => (
                  <label key={item.value} className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={form.withdrawAllowDays.includes(item.value)}
                      onChange={() =>
                        updateField(
                          'withdrawAllowDays',
                          toggleWithdrawDay(form.withdrawAllowDays, item.value)
                        )
                      }
                    />
                    {item.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="withdrawNotAllowedMessage">非開放時段提示語</label>
              <textarea
                id="withdrawNotAllowedMessage"
                rows={3}
                value={form.withdrawNotAllowedMessage}
                onChange={(e) => updateField('withdrawNotAllowedMessage', e.target.value)}
                placeholder="前台在非允許時段提現時顯示"
              />
            </div>
          </section>

          <div className="filter-actions">
            <button type="button" className="btn btn-secondary" onClick={loadSetting} disabled={saving}>
              重新載入
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
